'use client';

import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Trophy, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTelegramStatus } from "@/lib/api";
import { isPremiumTier } from "@/lib/subscription";
import type { TelegramAccountStatus } from "@shared/types";

// Check if email is a system-generated Telegram placeholder
function isTelegramEmail(email: string | undefined): boolean {
  return !!email && email.startsWith('telegram_') && email.includes('@temp.');
}

// Get user display info based on auth method
function getUserDisplayInfo(
  email: string | undefined,
  telegramStatus: TelegramAccountStatus | undefined
): { displayName: string; authMethod: 'telegram' | 'email'; initial: string } {
  const isTelegram = isTelegramEmail(email);

  if (isTelegram && telegramStatus?.linked) {
    // Build display name from Telegram info
    const fullName = [telegramStatus.telegramFirstName, telegramStatus.telegramLastName]
      .filter(Boolean)
      .join(' ');

    if (fullName) {
      return {
        displayName: fullName,
        authMethod: 'telegram',
        initial: fullName[0].toUpperCase(),
      };
    }

    if (telegramStatus.telegramUsername) {
      return {
        displayName: `@${telegramStatus.telegramUsername}`,
        authMethod: 'telegram',
        initial: telegramStatus.telegramUsername[0].toUpperCase(),
      };
    }

    // Fallback for Telegram user with no name/username
    return {
      displayName: 'Telegram User',
      authMethod: 'telegram',
      initial: 'T',
    };
  }

  // Email user (or Telegram status not loaded yet)
  return {
    displayName: email || 'User',
    authMethod: 'email',
    initial: email?.[0]?.toUpperCase() || 'U',
  };
}

export function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch Telegram status only for authenticated users with Telegram email
  const { data: telegramStatus } = useQuery<TelegramAccountStatus>({
    queryKey: ['telegramStatus'],
    queryFn: getTelegramStatus,
    enabled: isAuthenticated && isTelegramEmail(user?.email),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Success",
        description: "You have been logged out.",
      });
      setLocation("/");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to logout",
        variant: "destructive",
      });
    }
  };

  const handleSectionNavigation = (sectionId: string) => {
    setMobileMenuOpen(false);
    if (location !== '/') {
      setLocation('/');
      // Scroll after navigation completes
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // Already on home page, just scroll
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleHomeClick = () => {
    setMobileMenuOpen(false);
    if (location !== '/') {
      setLocation('/');
      // Scroll to top after navigation completes
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      // Already on home page, just scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get display info based on auth method
  const { displayName, authMethod, initial: userInitial } = getUserDisplayInfo(user?.email, telegramStatus);

  // Build membership label
  const membershipLabel = isPremiumTier(user?.subscriptionTier)
    ? "Premium"
    : "Free";

  // Build secondary line for dropdown
  const secondaryLine = authMethod === 'telegram'
    ? `via Telegram · ${membershipLabel}`
    : `${membershipLabel} Member`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-md">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div onClick={handleHomeClick} className="flex items-center gap-2 group cursor-pointer">
          <Trophy className="h-6 w-6 text-primary group-hover:text-primary/80 transition-colors" />
          <span className="font-display font-bold text-xl tracking-wider text-white">BETTING BRAIN</span>
        </div>

        <div className="hidden md:flex items-center gap-8">
          <button onClick={handleHomeClick} className="text-sm font-medium text-muted-foreground hover:text-white hover:opacity-90 transition-colors cursor-pointer">
            Home
          </button>
          <button onClick={() => handleSectionNavigation('tips')} className="text-sm font-medium text-muted-foreground hover:text-white hover:opacity-90 transition-colors cursor-pointer">
            Daily Tips
          </button>
          <Link href="/live-tips">
            <button className="text-sm font-medium text-muted-foreground hover:text-white hover:opacity-90 transition-colors cursor-pointer flex items-center gap-2">
              Live Tips
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
            </button>
          </Link>
          <button onClick={() => handleSectionNavigation('pricing')} className="text-sm font-medium text-muted-foreground hover:text-white hover:opacity-90 transition-colors cursor-pointer">
            Pricing
          </button>
        </div>

        {/* Mobile Menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] sm:w-[400px]">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                <span className="font-display font-bold text-lg tracking-wider">BETTING BRAIN</span>
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-4 mt-8">
              <button
                onClick={handleHomeClick}
                className="text-left px-4 py-3 text-base font-medium text-muted-foreground hover:text-white hover:bg-primary/10 rounded-md transition-colors"
              >
                Home
              </button>
              <button
                onClick={() => handleSectionNavigation('tips')}
                className="text-left px-4 py-3 text-base font-medium text-muted-foreground hover:text-white hover:bg-primary/10 rounded-md transition-colors"
              >
                Daily Tips
              </button>
              <Link href="/live-tips">
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full text-left px-4 py-3 text-base font-medium text-muted-foreground hover:text-white hover:bg-primary/10 rounded-md transition-colors flex items-center gap-2"
                >
                  Live Tips
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                </button>
              </Link>
              <button
                onClick={() => handleSectionNavigation('pricing')}
                className="text-left px-4 py-3 text-base font-medium text-muted-foreground hover:text-white hover:bg-primary/10 rounded-md transition-colors"
              >
                Pricing
              </button>

              {isAuthenticated && (
                <>
                  <div className="border-t border-white/10 my-2"></div>
                  <Link href="/account">
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-left px-4 py-3 text-base font-medium text-muted-foreground hover:text-white hover:bg-primary/10 rounded-md transition-colors"
                    >
                      My Account
                    </button>
                  </Link>
                  {!isPremiumTier(user?.subscriptionTier) && (
                    <button
                      onClick={() => handleSectionNavigation('pricing')}
                      className="text-left px-4 py-3 text-base font-bold text-primary hover:bg-primary/10 rounded-md transition-colors border border-primary"
                    >
                      GO PREMIUM
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="text-left px-4 py-3 text-base font-medium text-red-500 hover:bg-red-500/10 rounded-md transition-colors flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </>
              )}

              {!isAuthenticated && (
                <>
                  <div className="border-t border-white/10 my-2"></div>
                  <Link href="/login">
                    <Button
                      variant="ghost"
                      className="w-full font-bold tracking-wide justify-start px-4"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Button>
                  </Link>
                  <Link href="/register">
                    <Button
                      variant="default"
                      className="w-full font-bold tracking-wide"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex items-center gap-4">
          {isAuthenticated ? (
            <>
              {!isPremiumTier(user?.subscriptionTier) && (
                <Button
                  onClick={() => handleSectionNavigation('pricing')}
                  variant="outline"
                  className="font-bold tracking-wide hidden sm:inline-flex"
                  data-testid="button-gopremium"
                >
                  GO PREMIUM
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="rounded-full w-10 h-10 p-0 bg-primary/20 hover:bg-primary/30"
                    title={displayName}
                  >
                    <span className="text-sm font-bold text-primary">{userInitial}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{displayName}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {secondaryLine}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <Link href="/account">
                    <DropdownMenuItem className="cursor-pointer">My Account</DropdownMenuItem>
                  </Link>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-500">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link href="/login">
                <Button variant="ghost" className="font-bold tracking-wide">
                  Login
                </Button>
              </Link>
              <Link href="/register">
                <Button variant="default" className="font-bold tracking-wide" data-testid="button-getstarted">
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
